import React, { useState } from "react";
import { Calculator, ChevronDown, ChevronUp, X } from "lucide-react";
import { PeriodDelayCalculator } from "../dosage-calculators/PeriodDelayCalculator";
import { WeightLossCalculator } from "../dosage-calculators/WeightLossCalculator";
import { TravelHealthCalculator } from "../dosage-calculators/TravelHealthCalculator";
import { CalculatorResult, DosageCalculator } from "../dosageCalculators/types";
import { Button } from "@/components/ui/button";
import {
  addPatientVitals,
  getPatientVitals,
  updateConsultationActions,
  setVitalsDetails,
  setIncrementVitalsCompleted,
  setQuickActionsTabSwitch,
  trackServiceBilling,
  setAddPatientAIChatMessage,
  getPatientConsultationByConsultationId,
  SaveAIChatMessage,
} from "../../../../../dispatchers/patientDispatchers";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { ActionType } from "../../../../../enum/ActionType";

type WeightLossVitalPayload = {
  payload: any;
  vitalType: string;
  vitalTypeKey: string;
};

const formatWeightLossVitalsAsSeparate = (
  vitals: Array<{ type: string; value: string; unit?: string }>,
  patientInfo: any,
): WeightLossVitalPayload[] => {
  const recordedAt = new Date().toLocaleString();
  const vitalPayloads: WeightLossVitalPayload[] = [];

  // Process each vital from the calculator separately
  vitals.forEach((vital) => {
    let obj: any = {
      patientId: String(patientInfo.patientID),
      patientName: String(patientInfo.patientName || ""),
      recordedAt: recordedAt,
    };
    switch (vital.type.toLowerCase()) {
      case "weight":
        obj.vitalType = "Weight";
        obj.vitalTypeKey = "weight";
        obj.weight = {
          Value: String(vital.value),
          Unit: String(vital.unit || "kg"),
          recordedAt: recordedAt,
        };
        vitalPayloads.push({
          payload: obj,
          vitalType: "Weight",
          vitalTypeKey: "weight",
        });
        break;

      case "height":
        obj.vitalType = "Height";
        obj.vitalTypeKey = "height";
        obj.height = {
          Value: String(vital.value),
          Unit: String(vital.unit || "cm"),
          recordedAt: recordedAt,
        };
        vitalPayloads.push({
          payload: obj,
          vitalType: "Height",
          vitalTypeKey: "height",
        });
        break;

      case "bmi":
        obj.vitalType = "BMI Calculator";
        obj.vitalTypeKey = "bmicalculator";
        obj.bmi = {
          Value: String(vital.value),
          Unit: String(vital.unit || "kg/m²"),
          recordedAt: recordedAt,
        };
        vitalPayloads.push({
          payload: obj,
          vitalType: "BMI Calculator",
          vitalTypeKey: "bmicalculator",
        });
        break;

      case "bloodpressure":
        // vital.value should be in format "120/80"
        const bpParts = vital.value.split("/");
        if (bpParts.length === 2) {
          obj.vitalType = "Blood Pressure";
          obj.vitalTypeKey = "bloodpressure";
          obj.bloodPressure = {
            Systolic: {
              Value: String(bpParts[0]),
              Unit: "mmHg",
              recordedAt: recordedAt,
            },
            Diastolic: {
              Value: String(bpParts[1]),
              Unit: "mmHg",
              recordedAt: recordedAt,
            },
            recordedAt: recordedAt,
          };
          vitalPayloads.push({
            payload: obj,
            vitalType: "Blood Pressure",
            vitalTypeKey: "bloodpressure",
          });
        }
        break;

      case "pulse":
      case "heartrate":
        obj.vitalType = "Heart Rate";
        obj.vitalTypeKey = "heartrate";
        obj.heartRate = {
          Value: String(vital.value),
          Unit: "BPM",
          recordedAt: recordedAt,
        };
        vitalPayloads.push({
          payload: obj,
          vitalType: "Heart Rate",
          vitalTypeKey: "heartrate",
        });
        break;

      case "circumference":
        obj.vitalType = "Circumference";
        obj.vitalTypeKey = "circumference";
        obj.circumference = {
          Unit: String(vital.unit || "cm"),
          Value: `Waist ${vital.value}`,
          recordedAt: recordedAt,
        };
        vitalPayloads.push({
          payload: obj,
          vitalType: "Circumference",
          vitalTypeKey: "circumference",
        });
        break;
    }
  });

  return vitalPayloads;
};

interface CalculatorWidgetProps {
  calculator: DosageCalculator;
  isDarkMode: boolean;
  patientInfo?: any;
  onCalculatorComplete?: (results: CalculatorResult[]) => void;
  onCreatePrescriptionDraft?: (results: CalculatorResult[]) => void;
  onCreateVaccineDraft?: (results: CalculatorResult[]) => void;
  onSaveVitals?: (vital: any) => void;
  onClose?: () => void;
  consultationData?: any;
}

export const CalculatorWidget: React.FC<CalculatorWidgetProps> = ({
  calculator,
  isDarkMode,
  patientInfo,
  onCalculatorComplete,
  onCreatePrescriptionDraft,
  onCreateVaccineDraft,
  onSaveVitals,
  onClose,
  consultationData,
}) => {
  const { appState, patientState } = useTitanApp();
  const { currentPharmacyId, companyId } = appState || {};
  const { patientVitals, consultationId } = patientState || {};

  const [calculatorResults, setCalculatorResults] = useState<
    CalculatorResult[] | null
  >(null);
  const [resetKey, setResetKey] = useState(0);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const saveWeightLossVitals = async (results: CalculatorResult[]) => {
    if (!results[0].vitals || !patientInfo?.patientID) return;

    try {
      // Format each vital separately
      const vitalPayloads = formatWeightLossVitalsAsSeparate(
        results[0].vitals,
        patientInfo,
      );

      const recordedAt = new Date().toISOString();

      // Save each vital separately
      for (const vitalItem of vitalPayloads) {
        // Call add patient vitals API for each vital
        await addPatientVitals(vitalItem.payload);

        // Update consultation actions if in consultation mode
        if (consultationId) {
          // Create vitals payload for chat message (matches VitalsSection pattern)
          const vitalsPayloadForChat = {
            payload: JSON.stringify({
              vitalsDetails: {
                ...vitalItem.payload,
                recordedVitalType: vitalItem.vitalTypeKey,
              },
            }),
            actionName: ActionType[5],
            actionNumber: "5",
            isPerformed: true,
            performedAt: recordedAt,
          };

          // Update consultation actions
          await updateConsultationActions({
            consultationId: consultationId,
            payload: vitalsPayloadForChat,
          });

          // Add patient AI chat message explicitly for each vital
          setAddPatientAIChatMessage({
            sender: "success",
            message: "Vitals recorded successfully.",
            actionId: 5,
            details: vitalsPayloadForChat,
            vitalMessageId: `vital-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`,
          });

          // Save to consultation chat with proper structure
          await SaveAIChatMessage({
            ConsultationChat: JSON.stringify([
              {
                sender: "success",
                message: "Vitals recorded successfully.",
                actionId: 5,
                details: vitalsPayloadForChat,
              },
            ]),
            ConsultationId: consultationId,
          });

          // Track service billing for each vital
          try {
            await trackServiceBilling({
              companyId,
              consultationId,
              pharmacyId: currentPharmacyId,
              actiontype: vitalItem.vitalType.toLowerCase(),
            });
          } catch (err) {
            console.error(
              `Error tracking ${vitalItem.vitalType} billing:`,
              err,
            );
          }
        }
      }

      // Fetch updated consultation after all vitals are saved
      if (consultationId) {
        await getPatientConsultationByConsultationId(consultationId);
      }

      // Fetch updated vitals
      await getPatientVitals(patientInfo.patientID);

      // Update state indicators
      setQuickActionsTabSwitch(0);
    } catch (error) {
      console.error("Failed to save vitals:", error);
    }
  };

  const handleComplete = async (results: CalculatorResult[]) => {
    // For Travel Health Calculator, auto-create drafts and collapse immediately
    if (calculator.id === "travelHealth") {
      // Separate vaccine results from prescription results
      const vaccineResults = results.filter((r) => r.type === "vaccine");
      const prescriptionResults = results.filter(
        (r) => r.type === "prescription",
      );

      try {
        if (vaccineResults.length > 0 && onCreateVaccineDraft) {
          await onCreateVaccineDraft(vaccineResults);
        }
        if (prescriptionResults.length > 0 && onCreatePrescriptionDraft) {
          await onCreatePrescriptionDraft(prescriptionResults);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to create drafts in travel health:", error);
      }

      onCalculatorComplete?.(results);
      onClose?.();
    } else {
      // For other calculators, show the results screen
      setCalculatorResults(results);
      onCalculatorComplete?.(results);
    }
  };

  const handleCancel = () => {
    onClose?.();
    setCalculatorResults(null);
  };

  const handleBackToEdit = () => {
    // Just clear results, keep calculator state (user stays on their current step)
    setCalculatorResults(null);
  };

  const handleReset = () => {
    // Clear results AND reset calculator to initial state
    setCalculatorResults(null);
    setResetKey((prev) => prev + 1);
  };

  const handleCreateDraftClick = async () => {
    if (!calculatorResults) return;

    setIsCreatingDraft(true);
    try {
      // For Weight Loss Calculator, save vitals first
      if (calculator.id === "weightLoss") {
        await saveWeightLossVitals(calculatorResults);
      }

      // TravelHealth calculator creates both vaccines and prescriptions
      if (calculator.id === "travelHealth") {
        const vaccineResults = calculatorResults.filter(
          (r) => r.type === "vaccine",
        );
        const prescriptionResults = calculatorResults.filter(
          (r) => r.type === "prescription",
        );

        if (vaccineResults.length > 0 && onCreateVaccineDraft) {
          await onCreateVaccineDraft(vaccineResults);
        }
        if (prescriptionResults.length > 0 && onCreatePrescriptionDraft) {
          await onCreatePrescriptionDraft(prescriptionResults);
        }
      }
      // Vaccination calculator creates vaccine drafts, others create prescription drafts
      else if (calculator.id === "vaccination" && onCreateVaccineDraft) {
        await onCreateVaccineDraft(calculatorResults);
      } else if (onCreatePrescriptionDraft) {
        await onCreatePrescriptionDraft(calculatorResults);
      }

      onClose?.();
    } catch (error) {
      console.error("Failed to create draft:", error);
    } finally {
      setIsCreatingDraft(false);
    }
  };

  return (
    <div className={`flex flex-col h-full`}>
      {/* Header */}
      <div
        className={`p-1 flex items-center justify-between border-b sticky top-0 z-10 ${
          isDarkMode ? "bg-[#121212]" : "bg-white"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0`}
          >
            <Calculator className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h3 className={`text-sm font-semibold`}>{calculator.name}</h3>
          </div>
        </div>
        {onClose && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className={`flex items-center gap-1 p-1.5 rounded-md transition-colors ${
              isDarkMode
                ? "hover:bg-gray-700 text-gray-400 hover:text-gray-200"
                : "hover:bg-gray-200 text-gray-600 hover:text-gray-900"
            }`}
            title="Remove calculator"
          >
            <X className="w-4 h-4" />
            <span className="text-sm">Dismiss</span>
          </button>
        )}
      </div>

      {/* Calculator Content — no overflow here; child calculators manage their own scroll */}
      <div
        className={`p-4 pt-0 flex-1 flex flex-col min-h-0 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
      >
        {calculatorResults ? (
          // Results view: scrollable results + pinned footer buttons
          <div className="flex flex-col flex-1 min-h-0">
            {/* Scrollable results */}
            <div className="flex-1 mt-5 overflow-y-auto">
              <div
                className={`rounded-lg p-4 ${
                  isDarkMode
                    ? "bg-green-900/20 border border-green-700"
                    : "bg-green-50 border border-green-200"
                }`}
              >
                <h4
                  className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-green-300" : "text-green-900"}`}
                >
                  {calculator.id === "vaccination"
                    ? "Recommended Vaccinations"
                    : calculator.id === "travelHealth"
                      ? "Travel Health Plan"
                      : "Recommended Prescription"}
                </h4>
                {calculatorResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`space-y-2 ${idx > 0 ? "mt-4 pt-4 border-t border-green-300" : ""}`}
                  >
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span
                          className={`font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {calculator.id === "vaccination"
                            ? "Vaccine:"
                            : "Medication:"}
                        </span>
                        <p
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          {result.medication}
                        </p>
                      </div>
                      <div>
                        <span
                          className={`font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Dosage:
                        </span>
                        <p
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          {result.dosage}
                        </p>
                      </div>
                      <div>
                        <span
                          className={`font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Quantity:
                        </span>
                        <p
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          {result.quantity}
                        </p>
                      </div>
                      {result.duration && (
                        <div>
                          <span
                            className={`font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {calculator.id === "vaccination"
                              ? "Schedule:"
                              : "Duration:"}
                          </span>
                          <p
                            className={
                              isDarkMode ? "text-gray-200" : "text-gray-900"
                            }
                          >
                            {result.duration}
                          </p>
                        </div>
                      )}
                    </div>
                    {result.notes && (
                      <div className="mt-3">
                        <span
                          className={`font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Notes:
                        </span>
                        <p
                          className={`text-sm mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"} whitespace-pre-line`}
                        >
                          {result.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Pinned footer buttons — outside scroll area */}
            <div
              className={`flex gap-2 pt-3 pb-2 flex-shrink-0 border-t mt-2 ${isDarkMode ? "bg-[#121212] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <Button
                onClick={handleBackToEdit}
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={isCreatingDraft}
              >
                Back
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={isCreatingDraft}
              >
                Recalculate
              </Button>
              <Button
                onClick={handleCreateDraftClick}
                size="sm"
                className="flex items-center justify-center flex-1 gap-2 text-white bg-green-600 hover:bg-green-700"
                disabled={isCreatingDraft}
              >
                {isCreatingDraft ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                    Creating...
                  </>
                ) : calculator.id === "vaccination" ? (
                  "Create Vaccine Draft"
                ) : calculator.id === "travelHealth" ? (
                  "Create Travel Plan"
                ) : (
                  "Create Prescription"
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Render the appropriate calculator — it manages its own scroll internally
          <div className="flex flex-col flex-1 min-h-0 mt-5">
            {calculator.id === "travelHealth" && (
              <TravelHealthCalculator
                key={resetKey}
                onComplete={handleComplete}
                onCancel={handleCancel}
                patientInfo={patientInfo}
                isDarkMode={isDarkMode}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            )}
            {calculator.id === "periodDelay" && (
              <PeriodDelayCalculator
                key={resetKey}
                onComplete={handleComplete}
                onCancel={handleCancel}
                patientInfo={patientInfo}
                isDarkMode={isDarkMode}
              />
            )}
            {calculator.id === "weightLoss" && (
              <WeightLossCalculator
                key={resetKey}
                onComplete={handleComplete}
                onCancel={handleCancel}
                patientInfo={patientInfo}
                isDarkMode={isDarkMode}
                patientVitals={patientVitals}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
